/**
 * /api/masters/* hooks — read-only access to the platform-managed master
 * catalog (industry, org_type, country, currency, timezone, locale, language,
 * team_size_range). Drives dropdowns in the onboarding wizard and settings.
 *
 * Backend writes are restricted to platform admins; this file deliberately
 * exposes only the GET hooks for everyone.
 */

import { useQuery } from '@tanstack/react-query';
import { getApiClient } from '@clarix/api-client';
import { useAuthStore } from '@clarix/auth-store';
import type { MastersByCategoryResponse } from '@clarix/shared-types';
import { unwrap, type ApiResponse } from './envelope';

export type MasterCategory =
  | 'industry'
  | 'org_type'
  | 'team_size_range'
  | 'country'
  | 'currency'
  | 'timezone'
  | 'locale'
  | 'language';

export function useMasters(category: MasterCategory) {
  const hasToken = useAuthStore((s) => !!s.token);
  return useQuery({
    queryKey: ['masters', category],
    queryFn: async (): Promise<MastersByCategoryResponse> => {
      const res = await getApiClient().get<ApiResponse<MastersByCategoryResponse>>(
        `/api/masters/${category}`,
      );
      return unwrap(res.data);
    },
    enabled: hasToken,
    // Master rows change rarely; sit on the cache aggressively.
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
  });
}
